import React, { createContext, useContext, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheckCircle, FiAlertCircle, FiInfo, FiX } from 'react-icons/fi';

const ToastContext = createContext();

const ICONS = {
  success: FiCheckCircle,
  error: FiAlertCircle,
  info: FiInfo,
};

const COLORS = {
  success: { bg: 'rgba(22,163,74,0.08)', border: 'rgba(22,163,74,0.25)', text: '#16a34a' },
  error: { bg: 'rgba(220,38,38,0.08)', border: 'rgba(220,38,38,0.25)', text: '#dc2626' },
  info: { bg: 'rgba(37,99,235,0.08)', border: 'rgba(37,99,235,0.25)', text: '#2563eb' },
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
  }, []);

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 380 }}>
        <AnimatePresence>
          {toasts.map(toast => {
            const Icon = ICONS[toast.type] || FiInfo;
            const color = COLORS[toast.type] || COLORS.info;
            return (
              <motion.div key={toast.id}
                initial={{ opacity: 0, x: 60, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 60, scale: 0.95 }}
                transition={{ duration: 0.25 }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
                  borderRadius: 12, background: 'var(--bg-card, #fff)', border: `1px solid ${color.border}`,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)', fontSize: 13, fontWeight: 500,
                  color: 'var(--text-primary, #1e293b)'
                }}
              >
                <Icon size={18} color={color.text} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1 }}>{toast.message}</span>
                <button onClick={() => dismiss(toast.id)} style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--text-muted, #94a3b8)', flexShrink: 0
                }}>
                  <FiX size={14} />
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
